import { Icon } from '@chakra-ui/icon';
import { Box, Flex, Text } from '@chakra-ui/layout';
import { Tooltip } from '@chakra-ui/react';
import { djs } from '@duke-hq/djs';
import {
  CircleCheck,
  CircleX,
  Icon as IconType,
  Mail,
  Phone,
  MessageSquare,
  AlertCircle,
} from 'lucide-react';
import { useEffect, useRef } from 'react';

export const WeeklyContactType = {
  OUTBOUND_CALL: 'OUTBOUND_CALL',
  OUTBOUND_EMAIL: 'OUTBOUND_EMAIL',
  SUPPORT_CHAT: 'SUPPORT_CHAT',
  SMS_MESSAGE: 'SMS_MESSAGE',
} as const;

export const WeeklyActivityState = {
  ACTIVITY_PERFORMED: 'ACTIVITY_PERFORMED',
  NO_ACTIVITY: 'NO_ACTIVITY',
  FUTURE_ACTIVITY: 'FUTURE_ACTIVITY',
  NO_CONTACT_DETAILS: 'NO_CONTACT_DETAILS',
  DISABLED_WEEK: 'DISABLED_WEEK',
} as const;

export interface ActivityTrackerLabels {
  noStudentDetails: string;
  unableToContact: string;
  activity: string;
  contacts: string;
}

const defaultLabels: ActivityTrackerLabels = {
  noStudentDetails: 'No student details.',
  unableToContact: 'Unable to contact.',
  activity: 'Activity',
  contacts: 'Contacts',
};

const WEEK_COLUMN_MIN_WIDTH = 32;
const LABEL_COLUMN_WIDTH = 96;
const LABEL_COLUMN_WIDTH_REM = '6rem';
const CONTAINER_PADDING = 12;

function getActivityContactIcon(contactType: string): typeof IconType {
  switch (contactType) {
    case WeeklyContactType.OUTBOUND_CALL:
      return Phone;
    case WeeklyContactType.OUTBOUND_EMAIL:
      return Mail;
    case WeeklyContactType.SUPPORT_CHAT:
      return MessageSquare;
    case WeeklyContactType.SMS_MESSAGE:
      return MessageSquare;
    default:
      return MessageSquare;
  }
}

export type ActivityTrackerProps = {
  weeks:
    | {
        activities: (keyof typeof WeeklyActivityState)[];
        contacts: (
          | {
              created_at?: string | Date;
              note_type: string;
              label: string;
              text?: string | null;
            }[]
          | null
        )[];
        scheduleWeeks: {
          weekNumber: number;
          weekStart: ReturnType<typeof djs>;
          weekEnd: ReturnType<typeof djs>;
          overdue: boolean;
        }[];
      }
    | undefined;
  currentSemester: number | null;
  labels?: Partial<ActivityTrackerLabels>;
};

function Activity({ activityType }: { activityType: string | undefined }) {
  const baseStyles = {
    justifyContent: 'center',
    alignItems: 'center',
    minH: '6',
    w: '100%',
    flexShrink: 0,
    backgroundColor: 'bg.subtle',
  };

  switch (activityType) {
    case WeeklyActivityState.ACTIVITY_PERFORMED:
      return (
        <Flex {...baseStyles} backgroundColor="success" aria-label="Activity Performed">
          <Icon
            as={CircleCheck}
            color="fg.inverse"
            fontSize="2xl"
            sx={{ '& circle': { display: 'none' } }}
          />
        </Flex>
      );

    case WeeklyActivityState.NO_ACTIVITY:
      return (
        <Flex {...baseStyles} backgroundColor="bg.warning-emphasis" aria-label="No Activity">
          <Icon
            as={CircleX}
            color="warning.emphasis"
            fontSize="2xl"
            sx={{ '& circle': { display: 'none' } }}
          />
        </Flex>
      );

    case WeeklyActivityState.NO_CONTACT_DETAILS:
      return (
        <Flex {...baseStyles} backgroundColor="bg.emphasis" aria-label="No Contact Details">
          <Text fontSize="xs">!</Text>
        </Flex>
      );

    case WeeklyActivityState.FUTURE_ACTIVITY:
      return <Flex {...baseStyles} aria-label="Future Activity Week" />;

    case WeeklyActivityState.DISABLED_WEEK:
    case undefined:
    default:
      return (
        <Flex {...baseStyles} aria-label="Disabled Week">
          <Box w="8px" h="2px" backgroundColor="border.emphasis" />
        </Flex>
      );
  }
}

type ContactData = {
  created_at?: string | Date;
  note_type: string;
  label: string;
};

function Contact({
  activityType,
  contacts,
  weekNumber,
  l,
}: {
  activityType: string | undefined;
  contacts: ContactData[];
  weekNumber: number;
  l: ActivityTrackerLabels;
}) {
  const containerStyles = {
    minH: '6',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  };

  const innerStyles = {
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  };

  const hasNoContactDetails = activityType === WeeklyActivityState.NO_CONTACT_DETAILS;

  if (!contacts?.length) {
    const tooltipContent = (
      <Box data-testid={`no-contact-details-tooltip-${weekNumber}`}>
        <Flex gap={2} py="1" px={2}>
          <Icon as={AlertCircle} fontSize="base" mt={1} />
          <Box>
            <Text fontSize="xs" fontWeight="medium">
              {l.noStudentDetails}
            </Text>
            <Text fontSize="xs" fontWeight="medium">
              {l.unableToContact}
            </Text>
          </Box>
        </Flex>
      </Box>
    );

    return (
      <Tooltip
        label={tooltipContent}
        isDisabled={!hasNoContactDetails}
        hasArrow
        placement="top"
        {...containerStyles}
      >
        <Flex {...innerStyles}>
          {hasNoContactDetails ? (
            <Flex rounded="full" backgroundColor="bg.muted" aria-label="No Contact Details">
              <Icon as={AlertCircle} sx={{ '& circle': { display: 'none' } }} fontSize="lg" />
            </Flex>
          ) : (
            <Text color="fg.muted">-</Text>
          )}
        </Flex>
      </Tooltip>
    );
  }

  const contactsTooltip = (
    <Box data-testid={`contact-details-tooltip-${weekNumber}`}>
      {contacts.map((contact, index) => (
        <Flex
          key={`${index}_${contact.created_at?.toString()}`}
          alignItems="center"
          gap={2}
          py="0.5"
        >
          <Text fontSize="xs" fontWeight="medium" whiteSpace="nowrap">
            {djs(contact.created_at).format('DD MMM YY')}
          </Text>
          <Icon as={getActivityContactIcon(contact.note_type)} fontSize="xs" flexShrink={0} />
          <Text fontSize="xs" fontWeight="medium" noOfLines={1} title={contact.label}>
            {contact.label}
          </Text>
        </Flex>
      ))}
    </Box>
  );

  return (
    <Flex {...containerStyles}>
      <Tooltip label={contactsTooltip} isDisabled={!contacts.length} hasArrow placement="top">
        <Flex
          rounded="full"
          backgroundColor="bg.muted"
          minW="1.125rem"
          minH="1.125rem"
          justifyContent="center"
          alignItems="center"
        >
          <Text fontSize="xs" fontWeight="medium" lineHeight="1" aria-label="Contact Count">
            {contacts.length}
          </Text>
        </Flex>
      </Tooltip>
    </Flex>
  );
}

function ActivityTracker({
  weeks,
  currentSemester,
  labels,
}: ActivityTrackerProps): React.ReactElement {
  const l = { ...defaultLabels, ...labels };
  const scrollRef = useRef<HTMLDivElement>(null);
  // Show 13 as default if there are no schedule weeks
  // Empty 13 weeks will be visible when the trpc response is loading
  const totalWeeks = weeks ? weeks.scheduleWeeks.length : 13;

  // Create placeholder weeks for loading state
  const placeholderWeeks = Array.from({ length: 13 }, (_, i) => ({
    weekNumber: i + 1,
    weekStart: djs(),
    weekEnd: djs(),
    overdue: false,
  }));

  const scheduleWeeks = weeks?.scheduleWeeks || placeholderWeeks;
  const minScrollableWidth = totalWeeks * WEEK_COLUMN_MIN_WIDTH + CONTAINER_PADDING * 2; // 12 px padding on each side
  const totalContainerWidth = minScrollableWidth + LABEL_COLUMN_WIDTH;
  const currentWeekIndex = scheduleWeeks.findIndex((week) =>
    djs().isBetween(week.weekStart, week.weekEnd, 'day', '[)'),
  );

  useEffect(() => {
    if (
      scrollRef.current != null &&
      (weeks?.scheduleWeeks?.some((item) => item.overdue === true) ?? false)
    ) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [weeks, totalWeeks]);

  return (
    <Flex
      backgroundColor="bg.surface"
      rounded={6}
      borderWidth={1}
      borderColor="border.default"
      flexDirection="column"
      w="100%"
      overflow="hidden"
      minW={totalContainerWidth < 520 ? '26rem' : '35rem'}
    >
      <Flex>
        <Flex
          flexDirection="column"
          w={LABEL_COLUMN_WIDTH_REM}
          flexShrink={0}
          borderRight="1px solid"
          borderColor="border.default"
          boxShadow="0 4px 5px 0 rgba(0, 0, 0, 0.25)"
          zIndex={1}
          position="relative"
        >
          <Box
            backgroundColor="bg.emphasis"
            px={3}
            py="1"
            mb="1"
            minH="10"
            display="flex"
            alignItems="center"
          >
            {currentSemester !== null ? (
              <Text
                fontWeight="semibold"
                fontSize="xs"
                color="fg.subtle"
                textAlign="right"
                w="100%"
              >
                Sem {currentSemester}
              </Text>
            ) : null}
          </Box>

          <Box px={3} py="1" display="flex" alignItems="center" minH="8">
            <Text fontWeight="semibold" fontSize="xs" color="fg.subtle" textAlign="center" w="100%">
              {l.activity}
            </Text>
          </Box>

          <Box px={3} py="1" display="flex" alignItems="center" minH="8" mb="1">
            <Text fontWeight="semibold" fontSize="xs" color="fg.subtle" textAlign="center" w="100%">
              {l.contacts}
            </Text>
          </Box>
        </Flex>

        <Box
          ref={scrollRef}
          flex={1}
          overflowX="auto"
          backgroundColor="bg.surface"
          mb="1"
          __css={{
            '&::-webkit-scrollbar': {
              h: '1',
            },
            '&::-webkit-scrollbar-track': {
              h: '1',
              mx: '1',
              bg: 'bg.emphasis',
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '10',
              bg: `fg.subtle`,
            },
          }}
        >
          <Flex>
            {scheduleWeeks.map((week, index) => {
              const weekNumber = week.weekNumber;
              const isFirst = index === 0;
              const isLast = index === scheduleWeeks.length - 1;
              const weekColumnWidth =
                totalContainerWidth < 520 ? 'auto' : `${WEEK_COLUMN_MIN_WIDTH / 16}rem`;
              const isOverdue = week.overdue;
              const isCurrentWeek = index === currentWeekIndex;

              return (
                <Flex
                  flexDirection="column"
                  minW={isFirst || isLast ? 'auto' : weekColumnWidth}
                  flex={1}
                  key={weekNumber}
                >
                  <Flex backgroundColor={'bg.emphasis'} mb="1">
                    <Flex
                      py="1"
                      gap={0}
                      minH="10"
                      minW={weekColumnWidth}
                      w="100%"
                      justifyContent="center"
                      alignItems="center"
                      textAlign="center"
                      position="relative"
                      backgroundColor={isOverdue === true ? '#E53E3E0A' : 'transparent'}
                    >
                      {Object.keys(weeks?.activities || {}).length > 0 && isCurrentWeek === true ? (
                        <Box
                          position="absolute"
                          top="-0.25rem"
                          left="50%"
                          transform="translateX(-50%)"
                          width="0"
                          height="0"
                          borderLeft="0.5rem solid transparent"
                          borderRight="0.5rem solid transparent"
                          borderTop="0.5rem solid"
                          borderTopColor="danger.deep"
                        />
                      ) : null}
                      {weekNumber === 0 ? (
                        <Tooltip label="Prior to course start date." hasArrow placement="top">
                          <Text
                            fontSize="xs"
                            fontWeight="semibold"
                            color="fg.muted"
                            aria-label={`Prior to course start date`}
                          >
                            Pre
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          color={isOverdue === true ? 'danger.deep' : 'fg.muted'}
                          aria-label={`Week ${weekNumber}`}
                        >
                          {weekNumber}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                  <Flex
                    key={`activity-${weekNumber}`}
                    aria-label={`Activity Week ${weekNumber}`}
                    py={1}
                  >
                    <Activity activityType={weeks?.activities?.[index]} />
                  </Flex>
                  <Box aria-label={`Contact Week ${weekNumber}`} py={1}>
                    <Contact
                      key={`contact-${weekNumber}`}
                      weekNumber={weekNumber}
                      contacts={weeks?.contacts[index] || []}
                      activityType={weeks?.activities?.[index]}
                      l={l}
                    />
                  </Box>
                </Flex>
              );
            })}
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
}

export default ActivityTracker;
